import { useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import {
  Badge,
  Button,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TableScrollArea,
  Tile,
  TileContent,
  TileDescription,
  TileHeader,
  TileTitle
} from '../ui'
import { api } from '../../lib/api'
import MfaPolicyPanel from './MfaPolicyPanel'

type MfaStatus = {
  totp_enabled: boolean
  email_enabled: boolean
  email_target_masked: string | null
  recovery_remaining: number
  required: boolean
}

type TrustedDevice = {
  id: number
  device_id: string
  label: string
  user_agent: string | null
  ip_last_anon: string | null
  last_used_at: string
  expires_at: string
  isCurrentDevice?: boolean
}

interface SecurityPanelProps {
  isAdmin: boolean
}

export default function SecurityPanel({ isAdmin }: SecurityPanelProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [status, setStatus] = useState<MfaStatus | null>(null)
  const [devices, setDevices] = useState<TrustedDevice[]>([])
  const [loading, setLoading] = useState(false)
  const [totpModalOpen, setTotpModalOpen] = useState(false)
  const [totpQr, setTotpQr] = useState<string | null>(null)
  const [totpCode, setTotpCode] = useState('')
  const [emailCode, setEmailCode] = useState('')
  const [disableCode, setDisableCode] = useState('')
  const [disableMethod, setDisableMethod] = useState<'totp' | 'email'>('totp')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [recoveryCodeInput, setRecoveryCodeInput] = useState('')
  const [regenerateMethod, setRegenerateMethod] = useState<'totp' | 'email'>('totp')
  const [emailActivateOpen, setEmailActivateOpen] = useState(false)
  const setupParamHandledRef = useRef(false)

  const loadData = async () => {
    setLoading(true)
    try {
      const [statusResponse, devicesResponse] = await Promise.all([
        api.get('/auth/mfa/status'),
        api.get('/auth/mfa/devices')
      ])
      setStatus(statusResponse.data?.data || null)
      setDevices(devicesResponse.data?.data || [])
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Sicherheitsdaten konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
  }, [])

  useEffect(() => {
    if (setupParamHandledRef.current) return
    const setupMode = searchParams.get('mfaSetup')
    if (!setupMode) return

    setupParamHandledRef.current = true
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('mfaSetup')
    setSearchParams(nextParams, { replace: true })

    if (setupMode === 'totp') {
      void startTotpSetup()
      return
    }
    if (setupMode === 'email') {
      void requestEmailSetup()
    }
  }, [searchParams, setSearchParams])

  const mfaEnabled = useMemo(
    () => Boolean(status?.totp_enabled || status?.email_enabled),
    [status]
  )

  const startTotpSetup = async () => {
    try {
      const response = await api.post('/auth/mfa/setup/totp')
      setTotpQr(response.data?.data?.qrDataUrl || null)
      setTotpCode('')
      setTotpModalOpen(true)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'TOTP-Setup konnte nicht gestartet werden')
    }
  }

  const activateTotp = async () => {
    try {
      const response = await api.post('/auth/mfa/activate/totp', { code: totpCode })
      setRecoveryCodes(response.data?.data?.recoveryCodes || null)
      setTotpModalOpen(false)
      toast.success('Authenticator-App aktiviert')
      await loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'TOTP-Aktivierung fehlgeschlagen')
    }
  }

  const requestEmailSetup = async () => {
    try {
      const response = await api.post('/auth/mfa/setup/email')
      const target = response.data?.data?.email_target_masked
      setEmailActivateOpen(true)
      toast.success(target ? `Code an ${target} gesendet` : 'E-Mail-Code gesendet')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'E-Mail-Setup fehlgeschlagen')
    }
  }

  const activateEmail = async () => {
    try {
      await api.post('/auth/mfa/activate/email', { code: emailCode })
      setEmailCode('')
      setEmailActivateOpen(false)
      toast.success('E-Mail-OTP aktiviert')
      await loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'E-Mail-Code ungueltig')
    }
  }

  const disableMfa = async () => {
    try {
      await api.post('/auth/mfa/disable', { method: disableMethod, code: disableCode })
      toast.success('2FA deaktiviert')
      setDisableCode('')
      await loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || '2FA konnte nicht deaktiviert werden')
    }
  }

  const revokeDevice = async (id: number) => {
    try {
      await api.delete(`/auth/mfa/devices/${id}`)
      setDevices((current) => current.filter((device) => device.id !== id))
      toast.success('Geraet entfernt')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Geraet konnte nicht entfernt werden')
    }
  }

  const revokeAllDevices = async () => {
    try {
      await api.post('/auth/mfa/devices/revoke-all')
      toast.success('Alle vertrauten Geraete wurden entfernt')
      await loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Geraete konnten nicht entfernt werden')
    }
  }

  const regenerateRecovery = async () => {
    try {
      const response = await api.post('/auth/mfa/recovery/regenerate', {
        method: regenerateMethod,
        code: recoveryCodeInput
      })
      setRecoveryCodeInput('')
      setRecoveryCodes(response.data?.data?.recoveryCodes || null)
      toast.success('Neue Wiederherstellungscodes erstellt')
      await loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Codes konnten nicht neu erstellt werden')
    }
  }

  return (
    <div className="space-y-4">
      <Tile>
        <TileHeader>
          <TileTitle as="h3">Zwei-Faktor-Authentifizierung</TileTitle>
          <TileDescription>
            {loading ? 'Lade Status...' : (status?.required ? 'Fuer deine Rolle verpflichtend.' : 'Optional, aber empfohlen.')}
          </TileDescription>
        </TileHeader>
        <TileContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant={mfaEnabled ? 'success' : 'warning'}>
              {mfaEnabled ? 'Aktiviert' : 'Nicht aktiviert'}
            </Badge>
            {status?.email_target_masked && (
              <span className="text-xs text-gray-600 dark:text-gray-300">{status.email_target_masked}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => { void startTotpSetup() }} variant="primary" size="sm">
              Authenticator einrichten
            </Button>
            <Button onClick={() => { void requestEmailSetup() }} variant="outline" size="sm">
              E-Mail-Code einrichten
            </Button>
          </div>
          {mfaEnabled && (
            <div className="rounded-md border border-gray-200 p-3 dark:border-gray-700">
              <p className="mb-2 text-xs text-gray-600 dark:text-gray-300">2FA deaktivieren (Code erforderlich)</p>
              <div className="grid gap-2 md:grid-cols-3">
                <select
                  value={disableMethod}
                  onChange={(event) => setDisableMethod(event.target.value as 'totp' | 'email')}
                  className="input-field"
                >
                  <option value="totp">Authenticator</option>
                  <option value="email">E-Mail</option>
                </select>
                <input
                  className="input-field"
                  value={disableCode}
                  onChange={(event) => setDisableCode(event.target.value)}
                  placeholder="Code"
                />
                <Button variant="danger" onClick={() => { void disableMfa() }}>
                  2FA deaktivieren
                </Button>
              </div>
            </div>
          )}
        </TileContent>
      </Tile>

      <Tile>
        <TileHeader>
          <TileTitle as="h3">Wiederherstellungscodes</TileTitle>
          <TileDescription>Noch verfuegbar: {status?.recovery_remaining ?? 0}</TileDescription>
        </TileHeader>
        <TileContent className="space-y-3">
          <div className="grid gap-2 md:grid-cols-3">
            <select
              value={regenerateMethod}
              onChange={(event) => setRegenerateMethod(event.target.value as 'totp' | 'email')}
              className="input-field"
            >
              <option value="totp">Authenticator</option>
              <option value="email">E-Mail</option>
            </select>
            <input
              className="input-field"
              value={recoveryCodeInput}
              onChange={(event) => setRecoveryCodeInput(event.target.value)}
              placeholder="Bestaetigungscode"
            />
            <Button onClick={() => { void regenerateRecovery() }} variant="outline">
              Neue Codes erstellen
            </Button>
          </div>

          {recoveryCodes && (
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
              <p className="mb-2 text-xs text-gray-600 dark:text-gray-300">
                Diese Codes werden nur einmal angezeigt:
              </p>
              <div className="grid gap-1 sm:grid-cols-2">
                {recoveryCodes.map((code) => (
                  <code key={code} className="rounded bg-white px-2 py-1 text-xs dark:bg-gray-800">
                    {code}
                  </code>
                ))}
              </div>
            </div>
          )}
        </TileContent>
      </Tile>

      <Tile>
        <TileHeader>
          <TileTitle as="h3">Vertraute Geraete</TileTitle>
          <TileDescription>Erste Nutzung pro Geraet wird mit 2FA bestaetigt.</TileDescription>
        </TileHeader>
        <TileContent className="space-y-3">
          <div className="flex justify-end">
            <Button variant="danger" size="sm" onClick={() => { void revokeAllDevices() }}>
              Alle Geraete abmelden
            </Button>
          </div>
          <TableScrollArea>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Geraet</TableHeaderCell>
                  <TableHeaderCell>IP</TableHeaderCell>
                  <TableHeaderCell>Zuletzt aktiv</TableHeaderCell>
                  <TableHeaderCell>Ablauf</TableHeaderCell>
                  <TableHeaderCell>Aktion</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span>{device.label || 'Unbekanntes Geraet'}</span>
                        {device.isCurrentDevice && <Badge variant="info">Aktuelles Geraet</Badge>}
                      </div>
                    </TableCell>
                    <TableCell>{device.ip_last_anon || '-'}</TableCell>
                    <TableCell>{device.last_used_at ? new Date(device.last_used_at).toLocaleString('de-DE') : '-'}</TableCell>
                    <TableCell>{device.expires_at ? new Date(device.expires_at).toLocaleDateString('de-DE') : '-'}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => { void revokeDevice(device.id) }}>
                        Entfernen
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableScrollArea>
        </TileContent>
      </Tile>

      {isAdmin && <MfaPolicyPanel />}

      <Modal
        isOpen={totpModalOpen}
        onClose={() => setTotpModalOpen(false)}
        title="Authenticator einrichten"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setTotpModalOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={() => { void activateTotp() }}>
              Aktivieren
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-800 dark:border-primary-800/60 dark:bg-primary-950/30 dark:text-primary-200">
            Empfohlen: <strong>FreeOTP / FreeOTP+</strong>. Alternativ funktionieren auch andere
            TOTP-Apps wie 2FAS oder Aegis.
          </div>
          {totpQr && (
            <img src={totpQr} alt="MFA QR Code" className="mx-auto h-48 w-48 rounded border border-gray-200 bg-white p-2" />
          )}
          <input
            className="input-field w-full"
            value={totpCode}
            onChange={(event) => setTotpCode(event.target.value)}
            placeholder="Code aus der App"
          />
        </div>
      </Modal>

      <Modal
        isOpen={emailActivateOpen}
        onClose={() => setEmailActivateOpen(false)}
        title="E-Mail-OTP bestaetigen"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setEmailActivateOpen(false)}>
              Schliessen
            </Button>
            <Button onClick={() => { void activateEmail() }}>
              Bestaetigen
            </Button>
          </div>
        }
      >
        <p className="mb-2 text-sm text-gray-600 dark:text-gray-300">
          Falls ein E-Mail-Code angefordert wurde, hier eingeben:
        </p>
        <input
          className="input-field w-full"
          value={emailCode}
          onChange={(event) => setEmailCode(event.target.value)}
          placeholder="123456"
        />
      </Modal>
    </div>
  )
}
